import { useState, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import { RootState } from "../../state/store";
import { useMediaQuery } from "../Hooks/useMediaQuery";
import { useLatestRef } from "../Hooks/useLatestRef";
import { availableCards, DashboardPlacement } from "./dashboardCards";
import { CardPlacement, isDefaultLayout, parseSavedLayout, resolveLayout, toSavedLayout } from "./dashboardLayout";
import { readStored, subscribeToStored, writeStored } from "./dashboardStorage";

const storageKeyFor = (userId: string) => `dashboardLayout:${userId}`;

export const CUSTOMISABLE_SCREEN = "(min-width: 80rem)";

export const useDashboardLayout = () => {
  const { user } = useSelector((s: RootState) => s.authReducer);
  const { enqueueSnackbar } = useSnackbar();
  const storageKey = user ? storageKeyFor(user.id) : null;
  const stored = useSyncExternalStore(subscribeToStored, () => readStored(storageKey));
  const customisable = useMediaQuery(CUSTOMISABLE_SCREEN, true);
  const [draft, setDraft] = useState<DashboardPlacement[] | null>(null);

  if (draft !== null && !customisable) setDraft(null);

  const cards = availableCards(user);
  const layout = draft ?? resolveLayout(cards, parseSavedLayout(stored));
  const editing = draft !== null;
  const editingRef = useLatestRef(editing);

  const save = (placements: CardPlacement[]) => {
    if (!storageKey) return false;

    const saved = isDefaultLayout(placements, cards) ? [] : toSavedLayout(placements);
    if (writeStored(storageKey, JSON.stringify(saved))) return true;

    enqueueSnackbar("Your dashboard layout could not be saved", { variant: "error" });
    return false;
  };

  const update = (change: (latest: DashboardPlacement[]) => DashboardPlacement[]) => {
    if (editingRef.current) {
      setDraft(current => current && change(current));
      return;
    }

    const latest = resolveLayout(cards, parseSavedLayout(readStored(storageKey)));
    const next = change(latest);
    if (next !== latest) save(next);
  };

  const setHidden = (key: string, hidden: boolean) =>
    update(latest => latest.some(placement => placement.key === key && placement.hidden !== hidden)
      ? latest.map(placement => placement.key === key ? { ...placement, hidden } : placement)
      : latest);

  const startEditing = () => setDraft(layout);

  const cancelEditing = () => setDraft(null);

  const finishEditing = () => {
    if (draft !== null && save(draft)) setDraft(null);
  };

  const resetToDefault = () => setDraft(resolveLayout(cards, []));

  return { layout, editing, customisable, update, setHidden, startEditing, cancelEditing, finishEditing, resetToDefault };
};
